// 응답 일관성 분석 엔진
// 같은 내용을 서로 다른 표현으로 물은 병렬 문항(groupId가 같은 문항)끼리
// 답이 얼마나 비슷한지(0~10점 척도의 점수 차이)를 계산해 일관성을 측정한다.

import { hollandQuestions } from '@/mocks/hollandQuestions';
import { valueQuestions } from '@/mocks/valueQuestions';

export interface AnswerConsistency {
  score: number; // 종합 일관성 (0~100)
  level: string;
  comment: string;
  isConsistent: boolean;
  hollandScore: number; // 흥미검사 일관성 (0~100)
  valueScore: number; // 가치관검사 일관성 (0~100)
}

/** 병렬 문항 그룹 단위 일관성 계산 (그룹 내 점수 범위가 작을수록 높음) */
function computeGroupConsistency(groups: Map<string, number[]>): number {
  const perGroup: number[] = [];
  groups.forEach((scores) => {
    if (scores.length < 2) return;
    const min = Math.min(...scores);
    const max = Math.max(...scores);
    const range = max - min; // 0 ~ 10
    perGroup.push(Math.max(0, 1 - range / 10));
  });
  if (perGroup.length === 0) return 100;
  return Math.round((perGroup.reduce((a, b) => a + b, 0) / perGroup.length) * 100);
}

function collectGroups(
  questions: { id: string; groupId: string }[],
  answers: Record<string, number>,
): Map<string, number[]> {
  const groups = new Map<string, number[]>();
  questions.forEach((q) => {
    const v = answers[q.id];
    if (v === undefined) return;
    const arr = groups.get(q.groupId) ?? [];
    arr.push(v);
    groups.set(q.groupId, arr);
  });
  return groups;
}

export function computeAnswerConsistency(
  holland: Record<string, number>,
  value: Record<string, number>,
): AnswerConsistency {
  const hollandScore = computeGroupConsistency(collectGroups(hollandQuestions, holland));
  const valueScore = computeGroupConsistency(collectGroups(valueQuestions, value));
  const score = Math.round((hollandScore + valueScore) / 2);

  let level: string;
  let comment: string;
  let isConsistent: boolean;

  if (score >= 80) {
    level = '매우 일관적';
    isConsistent = true;
    comment = '비슷한 내용의 문항에 일관되게 응답했어요. 결과의 신뢰도가 매우 높습니다.';
  } else if (score >= 60) {
    level = '대체로 일관적';
    isConsistent = true;
    comment = '대체로 일관되게 응답했어요. 결과를 충분히 신뢰할 수 있습니다.';
  } else {
    level = '일관성 낮음';
    isConsistent = false;
    comment = '같은 내용을 다르게 물은 문항에 답이 크게 갈렸어요. 솔직하고 정직하게 다시 응답하면 더 정확한 결과를 받을 수 있어요.';
  }

  return { score, level, comment, isConsistent, hollandScore, valueScore };
}